import { AxiosError } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { RolodexContext } from 'features/rolodex/RolodexProvider'
import React from 'react'
import useSWR from 'swr'

interface RolodexFilesCopyQueryBody {
  /** The ID of the entry to list files under */
  entry_id: string
  destination_file_path: string
  source?: string
  content_type?: string
  update?: boolean
  source_path?: string
}

// -------------------------------------------------------------

export function useGetRolodexFilesCopyFetcher(): (
  query: RolodexFilesCopyQueryBody
) => Promise<string> {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { apiURL } = React.useContext(RolodexContext)

  return React.useCallback(
    (query) => {
      return new Promise((resolve, reject) => {
        if (!isAuthenticated) {
          reject(new Error('User is not authenticated'))
          return
        }

        const body = JSON.stringify(query)

        const params = new URLSearchParams()
        if (query.source) params.append('source', query.source)
        if (query.source_path) params.append('source_path', query.source_path)
        if (query.content_type)
          params.append('content_type', query.content_type)
        if (query.update !== undefined)
          params.append('update', String(query.update))

        const queryString = params.toString()
        const url = `${apiURL}/api/v2/files/copy/${query.entry_id}/${query.destination_file_path}${queryString ? `?${queryString}` : ''}`

        return axios
          .post(url, body, {
            headers: { 'Content-Type': 'application/json' },
          })
          .then((res) => resolve(res.data))
          .catch((err) => reject(err))
      })
    },
    [apiURL, axios, isAuthenticated]
  )
}

// -------------------------------------------------------------

export const useRolodexFilesCopy = (
  query: RolodexFilesCopyQueryBody | undefined
): {
  /** The response from the API */
  response: string
  /** Whether the SWR query is still loading */
  isLoading: boolean
  /** Whether the SWR query is still validating */
  isValidating: boolean
  /** To account for the initial case before the query fires  */
  hasEverLoaded: boolean
  /** Any errors returned from SWR */
  error: AxiosError
  /** A callback for forcing a re-fetch */
  forceRefresh: () => Promise<void>
} => {
  //----------------------------------------------------------------------------

  const fetcherBase = useGetRolodexFilesCopyFetcher()
  const fetcher = React.useMemo(() => {
    if (!query) {
      return () => Promise.resolve('')
    }
    return () => fetcherBase(query)
  }, [fetcherBase, query])

  const { data, isLoading, isValidating, error, mutate } = useSWR(
    JSON.stringify(query),
    fetcher
  )
  const response = React.useMemo(() => data ?? '', [data])

  const forceRefresh = React.useCallback(
    async () => await mutate().then(() => {}),
    [mutate]
  )

  return {
    response,
    isLoading,
    isValidating,
    error,
    hasEverLoaded: !!data,
    forceRefresh,
  }
}
